"use client";

import Link from "next/link";
import {
  useCallback,
  useEffect,
  useState,
} from "react";

type EtiquetaCard = {
  id: number;
  nome: string;
  cor: string;
};

type Card = {
  arte_card_id: number;
  ftp: string | null;
  cliente_nome: string;
  produto_nome: string;
  quantidade: string | number | null;
  formato: string | null;
  prioridade: string;
  cliente_respondeu: boolean;
  possui_pendencia: boolean;
  etiquetas: EtiquetaCard[];

  versao_atual_id: number | null;
  versao_atual_numero: number | null;

  arquivo_principal_id: number | null;
  arquivo_principal_nome: string | null;
  arquivo_principal_mime_type: string | null;
  arquivo_principal_url: string | null;
};

type Etapa = {
  id: number;
  codigo: string;
  nome: string;
  ordem: number;
  cards: Card[];
};

type CardGrupo = {
  arteCardId: number;
  ftp: string | null;
  produtoNome: string;
  etapaCodigo: string;
  etapaNome: string;
};

type Grupo = {
  pedidoId: number;
  clienteId: number | null;
  clienteNome: string;
  totalCards: number;
  ultimaAtividade: string;
  cards: CardGrupo[];
};

type MiniaturaProps = {
  card: Card;
};

function MiniaturaCard({
  card,
}: MiniaturaProps) {
  const url = card.arquivo_principal_url;
  const mimeType =
    card.arquivo_principal_mime_type ?? "";

  if (!url) {
    return (
      <div className="flex h-40 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50">
        <div className="text-center">
          <div className="text-3xl">🖼️</div>

          <p className="mt-2 text-xs font-medium text-slate-400">
            Sem prévia
          </p>
        </div>
      </div>
    );
  }

  if (mimeType.startsWith("image/")) {
    return (
      <div className="relative h-40 overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
        <img
          src={url}
          alt={
            card.arquivo_principal_nome ??
            card.produto_nome
          }
          loading="lazy"
          className="h-full w-full object-contain"
        />

        {card.versao_atual_numero && (
          <span className="absolute bottom-2 right-2 rounded-md bg-slate-950/75 px-2 py-1 text-[10px] font-bold text-white">
            Versão {card.versao_atual_numero}
          </span>
        )}
      </div>
    );
  }

  if (mimeType === "application/pdf") {
    return (
      <div className="relative h-40 overflow-hidden rounded-lg border border-slate-200 bg-white">
        <iframe
          src={`${url}#page=1&toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
          title={
            card.arquivo_principal_nome ??
            "Prévia do PDF"
          }
          loading="lazy"
          tabIndex={-1}
          className="pointer-events-none h-[420px] w-full origin-top scale-[0.38]"
        />

        <div className="pointer-events-none absolute inset-0" />

        <span className="absolute left-2 top-2 rounded-md bg-red-600 px-2 py-1 text-[10px] font-bold text-white shadow-sm">
          PDF
        </span>

        {card.versao_atual_numero && (
          <span className="absolute bottom-2 right-2 rounded-md bg-slate-950/75 px-2 py-1 text-[10px] font-bold text-white">
            Versão {card.versao_atual_numero}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-40 items-center justify-center rounded-lg border border-slate-200 bg-slate-50">
      <div className="text-center">
        <div className="text-4xl">📄</div>

        <p className="mt-2 max-w-[220px] truncate px-3 text-xs font-medium text-slate-500">
          {card.arquivo_principal_nome ??
            "Arquivo da versão"}
        </p>
      </div>
    </div>
  );
}

function formatarPrioridade(
  prioridade: string
) {
  const valor = prioridade
    .trim()
    .toLowerCase();

  if (valor === "alta" || valor === "urgente") {
    return {
      texto: prioridade,
      classe:
        "bg-red-100 text-red-700",
    };
  }

  if (valor === "media" || valor === "média") {
    return {
      texto: prioridade,
      classe:
        "bg-amber-100 text-amber-700",
    };
  }

  return {
    texto: prioridade,
    classe:
      "bg-slate-100 text-slate-600",
  };
}

function classeEtiquetaKanban(
  cor: string
) {
  switch (
    cor.trim().toLowerCase()
  ) {
    case "vermelho":
      return "border-red-300 bg-red-100 text-red-700";

    case "amarelo":
      return "border-amber-300 bg-amber-100 text-amber-800";

    case "azul":
      return "border-blue-300 bg-blue-100 text-blue-700";

    case "verde":
      return "border-emerald-300 bg-emerald-100 text-emerald-700";

    case "roxo":
      return "border-violet-300 bg-violet-100 text-violet-700";

    case "laranja":
      return "border-orange-300 bg-orange-100 text-orange-700";

    default:
      return "border-slate-300 bg-slate-100 text-slate-700";
  }
}

export default function ArtesPage() {
  const [etapas, setEtapas] = useState<
    Etapa[]
  >([]);

  const [grupos, setGrupos] = useState<
  Grupo[]
>([]);

  const [erro, setErro] = useState("");
  const [carregando, setCarregando] =
    useState(true);

  const carregarKanban =
    useCallback(async () => {
      try {
        setErro("");
        setCarregando(true);

        const [
  respostaKanban,
  respostaGrupos,
] = await Promise.all([
  fetch(
    "/api/artes/kanban",
    {
      cache: "no-store",
    }
  ),

  fetch(
    "/api/artes/grupos",
    {
      cache: "no-store",
    }
  ),
]);

const dadosKanban =
  await respostaKanban.json();

const dadosGrupos =
  await respostaGrupos.json();

if (
  !respostaKanban.ok ||
  !dadosKanban.ok
) {
  throw new Error(
    dadosKanban.error ||
      "Falha ao carregar o Kanban."
  );
}

if (
  !respostaGrupos.ok ||
  !dadosGrupos.ok
) {
  throw new Error(
    dadosGrupos.error ||
      "Falha ao carregar os grupos."
  );
}

setEtapas(
  dadosKanban.etapas
);

setGrupos(
  Array.isArray(
    dadosGrupos.grupos
  )
    ? dadosGrupos.grupos
    : []
);
      } catch (error) {
        setErro(
          error instanceof Error
            ? error.message
            : "Não foi possível carregar o Kanban."
        );
      } finally {
        setCarregando(false);
      }
    }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void carregarKanban();
    }, 0);

    return () => {
      window.clearTimeout(timer);
    };
  }, [carregarKanban]);

  return (
    <main className="min-h-screen bg-slate-100 text-slate-900">
      <header className="border-b border-slate-200 bg-white px-6 py-5">
        <div className="mx-auto flex max-w-[1800px] items-center justify-between gap-4">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.18em] text-blue-700">
              Excellent OS
            </p>

            <h1 className="mt-1 text-2xl font-bold">
              Aprovação de Artes
            </h1>

            <p className="mt-1 text-sm text-slate-500">
              Criação, conferência e aprovação
              das artes.
            </p>
          </div>

          <button
            type="button"
            onClick={carregarKanban}
            disabled={carregando}
            className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {carregando
              ? "Atualizando..."
              : "Atualizar"}
          </button>
        </div>
      </header>

      {erro && (
        <div className="mx-auto mt-6 max-w-[1800px] px-6">
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {erro}
          </div>
        </div>
      )}

      {carregando && (
        <div className="mx-auto max-w-[1800px] p-6 text-sm text-slate-500">
          Carregando Kanban...
        </div>
      )}

{!carregando &&
  !erro &&
  grupos.length > 0 && (
    <section className="mx-auto max-w-[1800px] px-6 pt-6">
      <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
        <div className="mb-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-700">
              Grupos de pedidos
            </p>

            <h2 className="mt-1 text-lg font-bold text-slate-900">
              Pedidos com múltiplas artes
            </h2>
          </div>

          <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-blue-700">
            {grupos.length}
          </span>
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {grupos.map((grupo) => (
            <Link
              key={grupo.pedidoId}
              href={`/artes/grupos/${grupo.pedidoId}`}
              className="rounded-xl border border-blue-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-400 hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-blue-700">
                    Pedido #{grupo.pedidoId}
                  </p>

                  <h3 className="mt-1 font-bold text-slate-900">
                    {grupo.clienteNome}
                  </h3>
                </div>

                <span className="rounded-full bg-blue-100 px-2.5 py-1 text-xs font-bold text-blue-700">
                  {grupo.totalCards} artes
                </span>
              </div>

              <div className="mt-4 space-y-2">
                {grupo.cards.map((card) => (
                  <div
                    key={card.arteCardId}
                    className="flex items-center justify-between gap-3 text-xs"
                  >
                    <span className="font-semibold text-slate-700">
                      {card.ftp ?? "FTP não informado"}
                    </span>

                    <span className="text-right text-slate-500">
                      {card.etapaNome}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-4 border-t border-slate-100 pt-3 text-right text-xs font-bold text-blue-700">
                Abrir grupo →
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )}
  
      {!carregando && !erro && (
        <section className="overflow-x-auto p-6">
          <div className="mx-auto flex min-w-max max-w-[1800px] items-start gap-4">
            {etapas.map((etapa) => (
              <section
                key={etapa.id}
                className="w-80 shrink-0 rounded-2xl border border-slate-200 bg-slate-200/70 p-3"
              >
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h2 className="text-sm font-bold text-slate-800">
                    {etapa.nome}
                  </h2>

                  <span className="rounded-full bg-white px-2.5 py-1 text-xs font-bold text-slate-600">
                    {etapa.cards.length}
                  </span>
                </div>

                <div className="space-y-3">
                  {etapa.cards.length === 0 && (
                    <div className="rounded-xl border border-dashed border-slate-300 bg-white/60 p-5 text-center text-xs text-slate-400">
                      Nenhuma arte
                    </div>
                  )}

                  {etapa.cards.map((card) => {
                    const prioridade =
                      formatarPrioridade(
                        card.prioridade
                      );

                    return (
                      <Link
                        key={card.arte_card_id}
                        href={`/artes/card/${card.arte_card_id}`}
                        className="block rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md"
                      >
                        <MiniaturaCard card={card} />

                        {card.etiquetas.length > 0 && (
  <div className="mt-3 flex flex-wrap gap-1.5">
    {card.etiquetas.map(
      (etiqueta) => (
        <span
          key={etiqueta.id}
          className={[
            "rounded-md border px-2 py-1 text-[10px] font-bold uppercase",
            classeEtiquetaKanban(
              etiqueta.cor
            ),
          ].join(" ")}
        >
          {etiqueta.nome}
        </span>
      )
    )}
  </div>
)}

                        <div className="mt-3 flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-xs font-bold uppercase tracking-wide text-blue-700">
                              FTP{" "}
                              {card.ftp ||
                                "não informado"}
                            </p>

                            <h3
                              title={
                                card.produto_nome
                              }
                              className="mt-1 line-clamp-2 text-sm font-bold text-slate-900"
                            >
                              {card.produto_nome}
                            </h3>
                          </div>

                          <span
                            className={[
                              "shrink-0 rounded-full px-2 py-1 text-[10px] font-bold uppercase",
                              prioridade.classe,
                            ].join(" ")}
                          >
                            {prioridade.texto}
                          </span>
                        </div>

                        <div className="mt-3 space-y-1.5 text-xs text-slate-600">
                          <p className="line-clamp-2">
                            <strong>Cliente:</strong>{" "}
                            {card.cliente_nome}
                          </p>

                          <p>
                            <strong>
                              Quantidade:
                            </strong>{" "}
                            {card.quantidade ??
                              "Não informada"}
                          </p>

                          <p>
                            <strong>Formato:</strong>{" "}
                            {card.formato ||
                              "Não informado"}
                          </p>
                        </div>

                        <div className="mt-4 flex flex-wrap gap-1.5">
                          {card.cliente_respondeu && (
                            <span className="rounded-md bg-emerald-100 px-2 py-1 text-[10px] font-semibold text-emerald-700">
                              Cliente respondeu
                            </span>
                          )}

                          {card.possui_pendencia && (
                            <span className="rounded-md bg-amber-100 px-2 py-1 text-[10px] font-semibold text-amber-700">
                              Pendência
                            </span>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}